import { EventPayloadTypeDynamic, logAmplitudeEvent } from 'amplitude/amplitude'
import { Struct } from 'contexts/structs/types/types'
import { useUserActionsContext } from 'contexts/userActions/useUserActionsContext'
import { addDevTool } from 'debug/ostools'
import { shouldSuppressE2eOverlays } from 'e2e/shouldSuppressE2eOverlays'
import { authSelectors } from 'ducks/auth'
import { Logger } from 'opensolar-sdk'
import { PromoDialog, usePromoDialogStructs } from 'persistentContent/promoDialog/usePromoDialogStructs'
import {
  ToastNotification,
  useToastNotificationStructs,
} from 'persistentContent/toastNotification/useToastNotificationStructs'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  findHigherPriorityUnshownStruct,
  resolveVisibleStructTransition,
} from './resolveVisibleStructTransition'

type StructType = 'promo-dialog' | 'toast-notification'
type AmpString = 'in_app_promo_shown' | 'toast_notification_shown'
interface StructData {
  structSource: any
  shortLabel: string
  fullLabel: string
  keyPrefix: string
  logger: any
  amplitude: {
    shown: AmpString
  }
}
const getData = (structType: StructType): StructData => {
  switch (structType) {
    case 'promo-dialog':
      return {
        structSource: usePromoDialogStructs,
        shortLabel: 'promos',
        fullLabel: 'promo dialog',
        keyPrefix: 'os-iap-',
        logger: new Logger('OS.PromoDialog'),
        amplitude: {
          shown: 'in_app_promo_shown',
        },
      }
    case 'toast-notification':
      return {
        structSource: useToastNotificationStructs,
        shortLabel: 'toasts',
        fullLabel: 'toast notification',
        keyPrefix: 'os-toast-',
        logger: new Logger('OS.ToastNotification'),
        amplitude: {
          shown: 'toast_notification_shown',
        },
      }
  }
}
const getStructKey = (struct, keyPrefix) => `${keyPrefix}${struct.key}`

export const useVisibleStructLogic = (structType: StructType, additionalFilter?: (struct: Struct<any>) => boolean) => {
  const [open, setOpen] = useState(false)
  const structData = useMemo(() => getData(structType), [structType])
  const useStructSource = useMemo(() => structData?.structSource, [structData])
  const { structs: allStructs } = useStructSource()
  const [currentKey, setCurrentKey] = useState<string>()
  const userId = useSelector(authSelectors.getCurrentUser)?.id || -1

  // Allows for hiding the promo from the console
  const [forceHide, setForceHide] = useState(false)
  addDevTool(
    `${structData.shortLabel}.forceHide`,
    () => {
      setForceHide(true)
    },
    { help: `Hides the ${structData.fullLabel}` }
  )
  addDevTool(
    `${structData.shortLabel}.clearForceHide`,
    () => {
      setForceHide(false)
    },
    { help: `Unhides the ${structData.fullLabel} (if there are still ${structData.shortLabel} to show)` }
  )

  // Incrementing forceCheck allows us to 'lock in' the promos that we've seen so that
  // they aren't immediately shown again when the user navigates closes the promo dialog
  const [forceCheck, setForceCheck] = useState(0)

  const { loaded: actionsLoaded, checkAction, recordAction } = useUserActionsContext()

  // both derived from currentKey
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [currentItem, setCurrentItem] = useState<Struct<any> | undefined>()

  const hasBeenDismissedOrFiltered = (struct: Struct<PromoDialog | ToastNotification>) => {
    const storageKey = getStructKey(struct, structData.keyPrefix)

    // Apply additional filter if provided (e.g., promo type suppression)
    if (additionalFilter && !additionalFilter(struct)) {
      structData.logger.debug(`\t${structData.fullLabel} Filtered by additional filter: `, struct.key)
      return true
    }

    if (struct.data.show_again_rule !== 'always' && dismissedThisSession[storageKey]) {
      // Promo has already been shown this session
      structData.logger.debug(`\t${structData.fullLabel} Filtered, already shown this session: `, struct.key, {
        storageKey: storageKey,
      })
      return true
    }

    if (struct.data.show_again_rule === 'never') {
      if (!actionsLoaded) {
        // Don't show until actions are loaded
        structData.logger.debug(`\t${structData.fullLabel} Filtered, actions not loaded yet: `, struct.key)
        return true
      }
      if (checkAction(storageKey)) {
        // Promo has already been shown
        structData.logger.debug(`\t${structData.fullLabel} Filtered, already shown: `, struct.key, { storageKey })
        return true
      }
    }
    return false
  }

  const structsToDisplay = useMemo(() => {
    return allStructs.filter((struct) => !hasBeenDismissedOrFiltered(struct))
  }, [allStructs, actionsLoaded, forceCheck, additionalFilter]) // intentionally not including checkAction, allows for outro to complete uninterupted

  const showPromo = (struct: Struct<PromoDialog | ToastNotification>) => {
    if (shouldSuppressE2eOverlays()) {
      return
    }
    const index = structsToDisplay.findIndex((s) => s.key === struct.key)

    setCurrentKey(struct.key)
    setCurrentIndex(index)
    setCurrentItem(struct)

    const storageKey = getStructKey(struct, structData.keyPrefix)
    if (!hasShownThisSession(storageKey)) {
      trackShownThisSession(storageKey)

      let payload: EventPayloadTypeDynamic | undefined
      if (structType === 'toast-notification') {
        payload = { notif_id: struct.key }
      } else if (structType === 'promo-dialog') {
        payload = { promo_id: struct.key }
      } else {
        console.warn(`Unhandled structType: ${String(structType)}`)
      }

      if (payload) {
        logAmplitudeEvent(structData.amplitude.shown, payload)
      }
    }
    setOpen(true)
  }

  const trackDismissed = (promo: Struct<PromoDialog | ToastNotification> | undefined) => {
    if (!promo) return
    const key = getStructKey(promo, structData.keyPrefix)
    if (userId !== -1) recordAction(key)
    trackDismissedThisSession(key)
  }

  const trackAllDismissed = () => {
    structsToDisplay.forEach(trackDismissed)
  }

  // Sync visible struct with the condition-filtered list (e.g. hide after leaving a target page).
  useEffect(() => {
    if (!structsToDisplay) return

    if (currentItem && structType === 'promo-dialog') {
      const higherPriority = findHigherPriorityUnshownStruct<PromoDialog | ToastNotification>({
        structsToDisplay,
        currentKey: currentItem.key,
        isShownThisSession: (key) => hasShownThisSession(`${structData.keyPrefix}${key}`),
      })
      if (higherPriority) {
        structData.logger.warn(`Promo (${higherPriority.key}) was added at higher priority, switching to it`)
        showPromo(higherPriority)
        return
      }
    }

    const transition = resolveVisibleStructTransition<PromoDialog | ToastNotification>({
      structsToDisplay,
      currentItem,
    })

    switch (transition.type) {
      case 'none':
        break
      case 'update':
        setCurrentItem(transition.struct)
        break
      case 'show':
        structData.logger.info(
          currentItem
            ? `${structData.fullLabel} (${currentItem.key}) no longer matches conditions, switching to: `
            : `Selected ${structData.fullLabel}: `,
          transition.struct.key
        )
        showPromo(transition.struct)
        break
      case 'clear':
        structData.logger.info(`${structData.fullLabel} (${currentItem?.key}) no longer matches conditions, closing`)
        setCurrentItem(undefined)
        setCurrentKey(undefined)
        setOpen(false)
        break
    }
  }, [structsToDisplay, currentItem])

  return {
    open,
    setOpen,
    currentKey,
    setCurrentKey,
    forceHide,
    forceCheck,
    setForceCheck,
    currentIndex,
    currentItem,
    setCurrentItem,
    structsToDisplay,
    showPromo,
    trackAllDismissed,
    trackDismissed,
    trackingKey: currentItem ? getStructKey(currentItem, structData.keyPrefix) : undefined,
  }
}
//TODO: this should be cleared on logout
const dismissedThisSession: Record<string, true> = {}
const trackDismissedThisSession = (storageKey: string) => {
  dismissedThisSession[storageKey] = true
}

//TODO: this should be cleared on logout
const shownThisSession: Record<string, true> = {}
const trackShownThisSession = (storageKey: string) => {
  shownThisSession[storageKey] = true
}
const hasShownThisSession = (storageKey: string) => {
  return !!shownThisSession[storageKey]
}
